import { useEffect, useMemo, useState } from "react";
import {
  TouchableOpacity,
  View,
  FlatList,
  Image,
  Text,
  ImageBackground,
} from "react-native";

import SvaraModal from "../SvaraModal";
import { rankModalStyles } from "./styles";
import { rankings, RankingType } from "./types";
import Centered from "../../../../shared/Centered";
import RankDataComponent from "./RankDataComponent";
import { useDevice } from "../../../../utils/hooks/useDevice";
import {
  BtnCloseModal,
  RankingTabBtn,
  RankingTabBtnSelected,
  Timer,
} from "../../../../assets";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectRankError,
  selectRankItems,
  selectRankLoading,
  selectRankPeriod,
  selectTournamentEndAt,
  selectTournamentNow,
} from "../../../../store/rankings/rankingsReducer";
import {
  rankingsByPeriodRequest,
  rankingsRequest,
  rankingsTournamentsRequest,
} from "../../../../store/rankings/rankingsActions";
import ProfileModal from "../ProfileModal";

import { TournamentTimer } from "./TimerBox";

interface IProps {
  onClose: () => void;
}

const rankingTypeToMetric = (rType: RankingType) =>
  rType === RankingType.Total
    ? "total"
    : rType === RankingType.ChipsTotal
      ? "chips"
      : "exp";

function RankingModal({ onClose }: IProps) {
  const { isMobile, height, width, isXs } = useDevice();
  const [rankingType, setRankingType] = useState(RankingType.Total);
  const [period, setPeriod] = useState<"Daily" | "Weekly" | "Monthly">("Daily");
  const tabContainerScale = useMemo(() => (isXs ? width / 570 : 1), [width]);
  const [allRanks, setAllRanks] = useState(rankings);
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const tournamentEndAt = useSelector(selectTournamentEndAt);
  const tournamentNow = useSelector(selectTournamentNow);

  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (
      rankingType !== RankingType.Tournaments ||
      !tournamentEndAt ||
      !tournamentNow
    ) {
      setTimeLeft(0);
      return;
    }

    const end = new Date(tournamentEndAt).getTime();
    const serverNow = new Date(tournamentNow).getTime();
    const localStartedAt = Date.now();

    const update = () => {
      const elapsed = Date.now() - localStartedAt;
      const currentServerBasedNow = serverNow + elapsed;
      const diff = Math.max(0, end - currentServerBasedNow);
      setTimeLeft(diff);
    };

    update();

    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [rankingType, tournamentEndAt, tournamentNow]);

  const [selectedPlayer, setSelectedPlayer] = useState<{
    rank: number;
    playerId: string;
    username: string;
    avatarFileId: string | null;
    totalChips: number;
    level: number;
  } | null>(null);

  const items = useSelector(selectRankItems);

  const loading = useSelector(selectRankLoading);
  const error = useSelector(selectRankError);
  const selectedPeriod = useSelector(selectRankPeriod);

  useEffect(() => {
    if (rankingType === RankingType.ChipsTotal) {
      dispatch(rankingsByPeriodRequest({ period, _start: 0, _end: 50 }));
    } else if (rankingType === RankingType.Tournaments) {
      dispatch(rankingsTournamentsRequest({ _start: 0, _end: 50 }));
    } else {
      dispatch(rankingsRequest({ _start: 0, _end: 50 }));
    }
  }, [dispatch, rankingType, period]);

  const handleTabChange = (rType: RankingType) => {
    if (rankingType === rType) {
      return;
    }

    setRankingType(rType);

    if (rType === RankingType.ChipsTotal) {
      dispatch(rankingsByPeriodRequest({ period, _start: 0, _end: 50 }));
    } else if (rType === RankingType.Tournaments) {
      dispatch(rankingsTournamentsRequest({ _start: 0, _end: 50 }));
    } else {
      dispatch(rankingsRequest({ _start: 0, _end: 50 }));
    }
  };

  const PeriodTabs = () => {
    if (rankingType !== RankingType.ChipsTotal) return null;

    const Btn = ({
      val,
      label,
    }: {
      val: "Daily" | "Weekly" | "Monthly";
      label: string;
    }) => {
      const active = period === val;
      return (
        <TouchableOpacity
          onPress={() => setPeriod(val)}
          style={{
            paddingVertical: 6,
            paddingHorizontal: 12,
            marginHorizontal: 6,
            borderRadius: 8,
            backgroundColor: active ? "#f4c542" : "rgba(255,255,255,0.08)",
            borderWidth: 1,
            borderColor: active ? "#f4c542" : "rgba(255,255,255,0.2)",
          }}
        >
          <Text
            style={{ color: active ? "#231f20" : "#fff", fontWeight: "800" }}
          >
            {label}
          </Text>
        </TouchableOpacity>
      );
    };

    return (
      <View
        style={{
          flexDirection: "row",
          justifyContent: "center",
          marginBottom: 10,
        }}
      >
        <Btn val="Daily" label={"DAILY"} />
        <Btn val="Weekly" label={"WEEKLY"} />
        <Btn val="Monthly" label={"MONTHLY"} />
        {/* <Btn val="Daily" label={t("T_SITE_RANKING_DAILY") || "DAILY"} />
        <Btn val="Weekly" label={t("T_SITE_RANKING_WEEKLY") || "WEEKLY"} />
        <Btn val="Monthly" label={t("T_SITE_RANKING_MONTHLY") || "MONTHLY"} /> */}
      </View>
    );
  };
  const TAB_BAR_H = 40;
  const listH =
    height * 0.67 - (rankingType === RankingType.ChipsTotal ? TAB_BAR_H : 0);
  return (
    <SvaraModal
      visible
      onClose={onClose}
      closeIcon={BtnCloseModal}
      closeButtonSize="big"
      closeButtonStyles={{
        width: 42,
        height: 42,
        ...(isXs && { transform: [{ translateX: 11 }, { translateY: -10 }] }),
      }}
    >
      <View
        style={{
          justifyContent: isMobile ? "flex-start" : "center",
          paddingLeft: isMobile ? 10 : 0,
          ...rankModalStyles.tabs,
        }}
      >
        {rankingType === RankingType.Tournaments && (
          <View
            style={{
              position: "absolute",
              bottom: 30,
              right: 10,
              // alignItems: "flex-end",
              marginBottom: 8,
            }}
          >
            <TournamentTimer timeLeft={timeLeft} />
          </View>
        )}
        <View
          style={{
            transform: [
              { translateY: ((1 - tabContainerScale) / 2) * 34 },
              { translateX: -((1 - tabContainerScale) / 1.8) * 500 },
              { scale: tabContainerScale },
            ],
            ...rankModalStyles.tabsWrapper,
          }}
        >
          <TouchableOpacity
            onPress={() => handleTabChange(RankingType.Total)}
            activeOpacity={0.8}
            style={{
              ...rankModalStyles.touchableOpacity,
              left: 0,
              zIndex: rankingType === RankingType.Total ? 5 : 4,
              transform: [
                { scale: rankingType === RankingType.Total ? 1.1 : 1.05 },
              ],
            }}
          >
            <Image
              source={
                rankingType === RankingType.Total
                  ? RankingTabBtnSelected
                  : RankingTabBtn
              }
              resizeMode="stretch"
              style={{
                width: isMobile ? 160 : 181,
                ...rankModalStyles.tabImage,
              }}
            />
            <Centered style={rankModalStyles.tabTextWrapper}>
              <Text
                style={{
                  color:
                    rankingType === RankingType.Total ? "white" : "#fce599",
                  fontWeight: "900",
                  fontSize: isMobile ? 15 : 16,
                  textTransform: "uppercase",
                }}
                children={t("T_SITE_RANKING_BY_CHIPS_TOTAL")}
              />
            </Centered>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => handleTabChange(RankingType.ChipsTotal)}
            activeOpacity={0.8}
            style={{
              ...rankModalStyles.touchableOpacity,
              left: isMobile ? 155 : 172,
              zIndex: rankingType === RankingType.ChipsTotal ? 5 : 3,
              transform: [
                { scale: rankingType === RankingType.ChipsTotal ? 1.1 : 1.05 },
              ],
            }}
          >
            <Image
              source={
                rankingType === RankingType.ChipsTotal
                  ? RankingTabBtnSelected
                  : RankingTabBtn
              }
              resizeMode="stretch"
              style={{
                width: isMobile ? 154 : 181,
                ...rankModalStyles.tabImage,
              }}
            />
            <Centered style={rankModalStyles.tabTextWrapper}>
              <Text
                style={{
                  color:
                    rankingType === RankingType.ChipsTotal
                      ? "white"
                      : "#fce599",
                  fontWeight: "900",
                  fontSize: isMobile ? 15 : 16,
                  textTransform: "uppercase",
                }}
                children={t("T_SITE_RANKING_BY_CHIPS_WON")}
              />
            </Centered>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              setRankingType(RankingType.Tournaments);
              dispatch(rankingsTournamentsRequest({ _start: 0, _end: 50 }));
            }}
            activeOpacity={0.8}
            style={{
              ...rankModalStyles.touchableOpacity,
              left: isMobile ? 300 : 350,
              zIndex: rankingType === RankingType.Tournaments ? 2 : 2,
              transform: [
                { scale: rankingType === RankingType.Tournaments ? 1.1 : 1.05 },
              ],
            }}
          >
            <Image
              source={
                rankingType === RankingType.Tournaments
                  ? RankingTabBtnSelected
                  : RankingTabBtn
              }
              resizeMode="stretch"
              style={{ width: 181, ...rankModalStyles.tabImage }}
            />
            <Centered style={rankModalStyles.tabTextWrapper}>
              <Text
                style={{
                  color:
                    rankingType === RankingType.Tournaments
                      ? "white"
                      : "#fce599",
                  fontWeight: "900",
                  fontSize: isMobile ? 15 : 16,
                  textTransform: "uppercase",
                }}
                children={t("T_SITE_RANKING_TOURNAMENTS")}
              />
            </Centered>
          </TouchableOpacity>
        </View>
      </View>

      <PeriodTabs />
      <FlatList
        style={{
          height: listH,
          ...rankModalStyles.flatList,
        }}
        data={items}
        showsVerticalScrollIndicator={false}
        keyExtractor={(_, index) => index.toString()}
        renderItem={({ item, index }) => (
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() =>
              setSelectedPlayer({
                rank: item.rank ?? index + 1,
                playerId: item.id,
                username: item.username,
                avatarFileId: item.avatarFileId ?? null,
                totalChips: item.totalChips ?? item.chips ?? 0,
                level: item.level ?? 1,
              })
            }
          >
            <RankDataComponent
              position={index + 1}
              ranking={item}
              isMobile={isMobile}
              rankingType={rankingType}
            />
          </TouchableOpacity>
        )}
      />
      {selectedPlayer && (
        <ProfileModal
          onClose={() => setSelectedPlayer(null)}
          player={selectedPlayer}
        />
      )}
    </SvaraModal>
  );
}

export default RankingModal;
